/**
 * A small sample of fabric intended to demonstrate the look of a larger piece, paint, makeup, etc. applied to a surface or one skin to test or demonstrate the color.
 *
 * @module views/component/ProductSwatchesMobile
 * @memberof -Common
 */
import './ProductSwatchesMobile.scss';
import 'swiper/swiper-bundle.min.css';
import 'swiper/swiper.min.css';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import PropTypes from 'prop-types';
import SwiperCore, {
  Keyboard,
  Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import palette_variables from 'web-styles/src/_exports.module.scss';

import AsyncComponent from '@ulta/core/components/AsyncComponent/AsyncComponent';
import Button from '@ulta/core/components/Button/Button';
import Text from '@ulta/core/components/Text/Text';
import { useAppConfigContext } from '@ulta/core/providers/AppConfigProvider/AppConfigProvider';
import { useOverlay } from '@ulta/core/providers/OverlayProvider/OverlayProvider';

import ProductDimension from '../ProductDimension/ProductDimension';
import Swatch from '../Swatch/Swatch';
// install Swiper modules
SwiperCore.use( [Navigation, Keyboard] );


/**
 * Represents a ProductSwatchesMobile component for mobile
 *
 * @method
 * @param {ProductSwatchesMobileProps} props - React properties passed from composition
 * @returns ProductSwatchesMobile
 */
export const ProductSwatchesMobile = React.forwardRef( ( props, _ ) =>{
  const {
    closeAccessibilityAction,
    doneAction,
    height = defaultProps.height,
    invokeAction,
    onSwatchSelected,
    refetchContentId,
    shadeFinderAction,
    typeLabel,
    variants: incomingVariants = [],
    viewAllAction,
    viewAllLabel,
    width = defaultProps.width,
    handleFindationClick
  } = props;

  const buttonRef = useRef();
  const [swiperInstance, setSwiperInstance] = useState();
  const { openOverlay, updateContent, closeOverlay } = useOverlay();
  const [showModal, setShowModal] = useState( false );

  const [variants, setVariants] = useState( incomingVariants );
  const [previewVariants, setPreviewVariants] = useState( incomingVariants );
  const [selectedIndex, setSelectedIndex] = useState( variants.findIndex( item => item.selected ) );
  const [isPreviewingSwatch, setIsPreviewingSwatch] = useState( false );
  const selectedItem = variants[selectedIndex];
  const { findationApiKey, findationTestingMode } = useAppConfigContext();

  // We could have a new DXL model from SwatchDetails, or re-implement the LayerHost broadcast
  // so we dont intercept props. The migration to invokeAction (without a broadcast/refresh content id)
  // forces us to intercept props at this level and choose what to update for the mobile props
  // and inside the overlay.
  useEffect( () => {
    manageUpdateVariant( { incomingVariants, selectedItem, isPreviewingSwatch }, { setPreviewVariants, setVariants, setSelectedIndex } );
  }, [incomingVariants, selectedItem, isPreviewingSwatch, setPreviewVariants, setVariants, setSelectedIndex] );

  useEffect( () => {
    if( !swiperInstance ){
      return;
    }

    // Scroll swiper
    swiperInstance.slideTo( selectedIndex );
  }, [selectedIndex, swiperInstance] );


  // Create modal component
  const swatchModal = useMemo( () => {
    return (
      <AsyncComponent
        closeAccessibilityAction={ closeAccessibilityAction }
        doneAction={ doneAction }
        invokeAction={ invokeAction }
        moduleName='SwatchDetails'
        onSwatchSelected={ ( item ) => {
          setIsPreviewingSwatch( false );
          onSwatchSelected( item );
        } }
        setIsPreviewingSwatch={ setIsPreviewingSwatch }
        refetchContentId={ refetchContentId }
        typeLabel={ typeLabel }
        variants={ previewVariants }
        closeOverlay={ closeOverlay }
      />
    );
  }, [previewVariants] );
  // Update modal content on changes
  useEffect( () => {
    swatchModalUpdate( { swatchModal }, { updateContent } );
  }, [swatchModal, updateContent] );

  // Handle opening / closing modal
  useEffect( () => {
    if( !showModal ){
      setPreviewVariants( variants );
      return;
    }
    openOverlay( {
      type: 'flyout',
      crossButtonVisibility: true,
      opener: buttonRef,
      content: swatchModal
    } );
  }, [showModal] );

  const onClick = useCallback( () => {
    // in the OverlayProvider where onClose is called on each open, and not on close for modals.
    if( showModal ){
      setShowModal( false );
    }
    // Wait for DOM and OverlayProvider to process the close event, then show modal.
    setTimeout( () => {
      setShowModal( true );
    }, 0 );
  }, [showModal] );
  return (
    <div className='ProductSwatchesMobile'>
      <ProductDimension
        type={ typeLabel }
        description={ selectedItem?.name }
      />
      <Text htmlTag='p'
        textStyle='body-3'
        color='neutral-700'
      >{ selectedItem?.shadeDescription }</Text>
      <Swiper
        freeMode={ true }
        speed={ 600 }
        preloadImages={ true }
        spaceBetween={ 20 }
        breakpoints={ {
          [ parseInt( palette_variables.breakpointMD, 10 ) + 1]: {
            slidesPerView: 16.5
          },
          577: {
            slidesPerView: 12.5,
            spaceBetween: 10
          },
          [palette_variables.breakpointSM]: {
            slidesPerView: 7.5,
            spaceBetween: 10
          }
        } }
        onSwiper={ setSwiperInstance }
      >
        {
          variants && variants.map( ( item, index ) => (
            <SwiperSlide
              key={ index }
            >
              <Swatch
                key={ index }
                src={ item.swatchImage.imageUrl }
                alt={ item.swatchImage.description }
                selected={ item.selected }
                disabled={ item.disabled }
                unavailable={ item.unavailable }
                showIcon={ true }
                linkSelectAction={ item.linkSelectAction }
                selectAction={ item.selectAction }
                width={ width }
                height={ height }
                onSwatchClick={ () => onSwatchSelected( item ) }
                withHoverAndSelected={ true }
              />
            </SwiperSlide>
          ) )
        }

      </Swiper>
      <div className='ProductSwatchesMobile__optionAndShade'>
        <div className='ProductSwatchesMobile__optionAndShade__button'
          ref={ buttonRef }
          role='button'
          tabIndex={ 0 }
        >
          <Button
            likeLink
            action={ viewAllAction }
            label={ viewAllLabel }
            ariaLabel={ viewAllLabel }
            isSwatchModal={ true }
            onClick={ onClick }
          />
        </div>
        { shadeFinderAction?.label &&
          <Button
            likeLink
            label={ shadeFinderAction.label }
            onClick={ handleFindationClick }
          />
        }
      </div>
    </div>
  );
} );


/**
 * The contains the logic to update vraiants
 *
 * @method
 * @param { Object } data - incomingVariants, selectedItem, isPreviewingSwatch
 * @param { Object } methods -  setPreviewVariants, setVariants, setSelectedIndex
 * @returns string
**/
export const manageUpdateVariant = ( data, methods )=>{
  const { incomingVariants, selectedItem, isPreviewingSwatch } = data || {};
  const { setPreviewVariants, setVariants, setSelectedIndex } = methods || {};
  // Get selected swatch
  const selectedIndex = incomingVariants?.findIndex( item => item.selected );

  // If sku is already selected don't update swatches
  if( incomingVariants?.[selectedIndex]?.skuId === selectedItem?.skuId ){
    return;
  }

  // Update preview modal
  if( isPreviewingSwatch ){
    setPreviewVariants( incomingVariants );
    return;
  }

  // If a new swatch has been selected outside modal, update variants
  setVariants( incomingVariants );

  // Update selected
  setSelectedIndex( selectedIndex );
};

/**
 * Represents a swatchModalUpdate component for mobile
 *
 * @method
 * @param { Object } data - swatchModal
 * @param { Object } methods -  updateContent
 * @returns string
**/

export const swatchModalUpdate = ( data, methods )=>{
  const { swatchModal } = data || {};
  const { updateContent } = methods || {};
  if( !swatchModal ){
    return;
  }
  updateContent( swatchModal );
};

/**
 * Property type definitions
 *
 * @typedef ProductSwatchesMobileProps
 * @type {object}
 * @property {array} variants - This is the list of product swatches received from backend.
 * @property {object} shadeFinderAction - Hold the value label and url for shadeFinderAction.
 * @property {string} typeLabel - This holds the label for color of swatches.
 * @property {string} viewAllLabel - This is label for view all.
 * @property {number} width - This is the width of swatch.
 * @property {number} height - This is the height of swatch.
 * @property {object} doneAction - This is the doneAction for SwatchDetails.
 * @property {object} closeAccessibilityAction - This is the closeAccessibilityAction for SwatchDetails.
 * @property {function} invokeAction - Sets the selected variant data
 * @property {string} onSwatchSelected - Sets the selected variant
 * @property {string} refetchContentId - particular content id of selected variant
 * @property {function} viewAllAction - view all action
 */
export const propTypes = {

  /** This is the list of product received from backend for a specific query.*/
  variants: PropTypes.arrayOf(
    PropTypes.shape( {
      productId: PropTypes.string,
      skuId: PropTypes.string,
      name: PropTypes.string,
      shadeDescription: PropTypes.string,
      swatchImage: PropTypes.shape( {
        imageUrl: PropTypes.string,
        description: PropTypes.string
      } ),
      smooshImage: PropTypes.shape( {
        imageUrl: PropTypes.string,
        description: PropTypes.string
      } ),
      selected: PropTypes.bool,
      disabled: PropTypes.bool,
      unavailable: PropTypes.bool,
      listPrice: PropTypes.string,
      salePrice: PropTypes.string
    } )
  ),
  /** Hold the value fr url and label for shade finder link.*/
  shadeFinderAction: PropTypes.shape( {
    label: PropTypes.string,
    url: PropTypes.string
  } ),
  /** This sets the label for color text.*/
  typeLabel: PropTypes.string,
  /** This hold the label for view all app label */
  viewAllLabel: PropTypes.string,
  /** This is the width of swatch.*/
  width: PropTypes.number,
  /** This is the height of swatch.*/
  height: PropTypes.number,
  /** This is the doneAction for SwatchDetails.*/
  doneAction: PropTypes.object,
  /** This is the closeAccessibilityAction for SwatchDetails.*/
  closeAccessibilityAction: PropTypes.object
};

/**
 * Default values for passed properties
 * @type {object}
 * @property {number} width=40 - Default swatch width is 40
 * @property {number} height=40 - Default swatch height is 40
 */
export const defaultProps = {
  width: 40,
  height: 40
};

ProductSwatchesMobile.propTypes = propTypes;
ProductSwatchesMobile.defaultProps = defaultProps;
ProductSwatchesMobile.displayName = 'ProductSwatchesMobile';

export default ProductSwatchesMobile;
